import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route } from 'react-router-dom';
import { 
  FileText, 
  CheckCircle2,
  Clock,
  XCircle,
  MoreVertical,
  Eye,
  BookOpen
} from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  getApplications,
  updateApplicationStatus
} from '@/services/api';
import { toast } from 'sonner';

// Institution Overview
const InstitutionOverview = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadApplications = useCallback(async () => {
    try {
      const data = await getApplications();
      setApplications(data);
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const pendingCount = applications.filter(a => a.status === 'submitted' || a.status === 'under_review').length;
  const approvedCount = applications.filter(a => a.status === 'loa_issued').length;
  const enrolledCount = applications.filter(a => a.status === 'enrolled').length;

  return (
    <div className="space-y-6" data-testid="institution-overview">
      <div>
        <h1 className="text-2xl font-medium text-[white]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Institution Dashboard
        </h1>
        <p className="text-[#6B678A] text-sm mt-1">Review and manage applications</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-[#141029] border-[#2D2654]">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6B678A]">Pending Review</p>
                <p className="text-2xl font-semibold text-[white]">{pendingCount}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-[#F59E0B]" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#141029] border-[#2D2654]">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6B678A]">LOA Issued</p>
                <p className="text-2xl font-semibold text-[white]">{approvedCount}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-[#10B981]/10 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-[#10B981]" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#141029] border-[#2D2654]">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6B678A]">Enrolled</p>
                <p className="text-2xl font-semibold text-[white]">{enrolledCount}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-[#00D4FF]/10 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-[#00D4FF]" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Applications */}
      <Card className="bg-[#141029] border-[#2D2654]">
        <CardHeader>
          <CardTitle className="text-[white]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Recent Applications
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {applications.slice(0, 5).map((app) => (
              <div key={app.id} className="p-4 rounded-lg bg-[#0D0B1E] border border-[#2D2654] flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[white]">{app.student_name}</p>
                  <p className="text-xs text-[#6B678A]">{app.course_name}</p>
                </div>
                <Badge className={
                  app.status === 'submitted' ? 'bg-[#3B82F6]/20 text-[#60A5FA]' :
                  app.status === 'under_review' ? 'bg-[#F59E0B]/20 text-[#FBBF24]' :
                  app.status === 'loa_issued' ? 'bg-[#10B981]/20 text-[#34D399]' :
                  'bg-[#2D2654] text-[#B8B4CE]'
                }>
                  {app.status?.replace('_', ' ')}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Applications Management
const InstitutionApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const loadApplications = useCallback(async () => {
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const data = await getApplications(params);
      setApplications(data);
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const handleStatusUpdate = async (appId, newStatus) => {
    try {
      await updateApplicationStatus(appId, newStatus);
      toast.success('Status updated successfully');
      loadApplications();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const statusColors = {
    submitted: 'bg-[#3B82F6]/20 text-[#60A5FA]',
    under_review: 'bg-[#F59E0B]/20 text-[#FBBF24]',
    documents_required: 'bg-[#EF4444]/20 text-[#F87171]',
    loa_issued: 'bg-[#10B981]/20 text-[#34D399]',
    visa_filed: 'bg-[#8B5CF6]/20 text-[#A78BFA]',
    enrolled: 'bg-[#00D4FF]/20 text-[#00D4FF]',
    rejected: 'bg-[#EF4444]/20 text-[#F87171]'
  };

  return (
    <div className="space-y-6" data-testid="institution-applications">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium text-[white]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Application Review
          </h1>
          <p className="text-[#6B678A] text-sm mt-1">Review and process student applications</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48 bg-[#0D0B1E] border-[#2D2654] text-[white]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent className="bg-[#141029] border-[#2D2654]">
            <SelectItem value="all" className="text-[white]">All Applications</SelectItem>
            <SelectItem value="submitted" className="text-[white]">Submitted</SelectItem>
            <SelectItem value="under_review" className="text-[white]">Under Review</SelectItem>
            <SelectItem value="loa_issued" className="text-[white]">LOA Issued</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-[#141029] border-[#2D2654]">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#0D0B1E] border-b border-[#2D2654]">
                <tr>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">Student</th>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">Course</th>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">Status</th>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">Date</th>
                  <th className="text-right text-sm font-medium text-[#B8B4CE] px-6 py-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {applications.map((app) => (
                  <tr key={app.id} className="border-b border-[#2D2654] hover:bg-[#1A1435] transition-colors">
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-[white]">{app.student_name}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-[#B8B4CE]">{app.course_name}</p>
                    </td>
                    <td className="px-6 py-4">
                      <Badge className={statusColors[app.status] || 'bg-[#2D2654] text-[#B8B4CE]'}>
                        {app.status?.replace('_', ' ')}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-[#6B678A]">
                        {new Date(app.created_at).toLocaleDateString()}
                      </p>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-[#6B678A] hover:text-[white]">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-[#141029] border-[#2D2654]">
                          <DropdownMenuItem className="text-[white] focus:bg-[#1A1435]">
                            <Eye className="w-4 h-4 mr-2" /> View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-[white] focus:bg-[#1A1435]"
                            onClick={() => handleStatusUpdate(app.id, 'under_review')}
                          >
                            <Clock className="w-4 h-4 mr-2" /> Mark Under Review
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-[#10B981] focus:bg-[#1A1435]"
                            onClick={() => handleStatusUpdate(app.id, 'loa_issued')}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" /> Issue LOA
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-[#EF4444] focus:bg-[#1A1435]"
                            onClick={() => handleStatusUpdate(app.id, 'rejected')}
                          >
                            <XCircle className="w-4 h-4 mr-2" /> Reject
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
                {applications.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-[#6B678A]">
                      No applications found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Placeholder pages
const InstitutionCourses = () => (
  <div className="space-y-6">
    <h1 className="text-2xl font-medium text-[white]" style={{ fontFamily: 'Outfit, sans-serif' }}>Course Management</h1>
    <p className="text-[#6B678A]">Course management coming soon...</p>
  </div>
);

const InstitutionAnalytics = () => (
  <div className="space-y-6">
    <h1 className="text-2xl font-medium text-[white]" style={{ fontFamily: 'Outfit, sans-serif' }}>Analytics</h1>
    <p className="text-[#6B678A]">Institution analytics coming soon...</p>
  </div>
);

// Main Institution Dashboard
const InstitutionDashboard = () => {
  return (
    <DashboardLayout>
      <Routes>
        <Route path="/" element={<InstitutionOverview />} />
        <Route path="/applications" element={<InstitutionApplications />} />
        <Route path="/courses" element={<InstitutionCourses />} />
        <Route path="/analytics" element={<InstitutionAnalytics />} />
      </Routes>
    </DashboardLayout>
  );
};

export default InstitutionDashboard;
